import { alphabeticalSorterFor } from '../shared/TableLayout';
import Tag from 'antd/lib/tag';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import React, { useState } from 'react';
import { formatISODuration, truncateText } from '../../utils';

const MAX_VISIBLE_TAGS = 4;

type ExpandableTagsProps = {
  items: string[];
  intl: any;
  translationPrefix?: string;
  tagStyle: React.CSSProperties;
};

const ExpandableTags = ({
  items,
  intl,
  translationPrefix,
  tagStyle,
}: ExpandableTagsProps) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = items?.length > MAX_VISIBLE_TAGS;
  const visibleItems = expanded ? items : items?.slice(0, MAX_VISIBLE_TAGS);

  const getLabel = (item: string) => {
    const id = translationPrefix ? `${translationPrefix}${item}` : item;
    return intl.formatMessage({ id });
  };

  return (
    <div>
      {visibleItems?.map((item) => (
        <Tag key={item} style={tagStyle} className="merchant-tag">
          {getLabel(item)}
        </Tag>
      ))}
      {hasMore && (
        <span
          style={{
            cursor: 'pointer',
            color: '#1890ff',
            marginLeft: 4,
            userSelect: 'none',
          }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          {expanded ? <UpOutlined /> : <DownOutlined />}
          <span style={{ marginLeft: 4 }}>
            {expanded ? '' : `+${items.length - MAX_VISIBLE_TAGS}`}
          </span>
        </span>
      )}
    </div>
  );
};

const getPurposesColumns = (intl) => {
  const renderStartDate = (value) => (
    <Tag color="#e9d9c4" style={{ color: '#663403' }}>
      {intl.formatMessage({ id: `${value}` })}
    </Tag>
  );

  const renderCollections = (collectionNames: string[]) => (
    <ExpandableTags
      items={collectionNames}
      intl={intl}
      translationPrefix="privacy.collectionName."
      tagStyle={{
        color: '#1a5276',
        backgroundColor: '#d4e6f1',
        margin: 2,
      }}
    />
  );

  const renderGroups = (fieldGroupKeys: string[]) => (
    <ExpandableTags
      items={fieldGroupKeys}
      intl={intl}
      tagStyle={{
        color: '#666668',
        backgroundColor: '#e7e7e7',
        margin: 2,
      }}
    />
  );

  return [
    {
      title: intl.formatMessage({ id: 'fleetadmin.purpose.name' }),
      dataIndex: 'baseName',
      width: 200,
      fixed: 'left' as 'left',
      sorter: alphabeticalSorterFor('baseName'),
    },
    {
      title: intl.formatMessage({ id: 'fleetadmin.purpose.collectionName' }),
      dataIndex: 'collectionNames',
      render: renderCollections,
      sorter: alphabeticalSorterFor('collectionNames'),
    },
    {
      title: intl.formatMessage({ id: 'fleetadmin.purpose.startdate' }),
      dataIndex: 'activationEventKey',
      render: renderStartDate,
      sorter: alphabeticalSorterFor('activationEventKey'),
    },
    {
      title: intl.formatMessage({ id: 'fleetadmin.purpose.minduration' }),
      dataIndex: 'minDuration',
      render: (value) => <span>{formatISODuration(value)}</span>,
      sorter: alphabeticalSorterFor('minDuration'),
    },
    {
      title: intl.formatMessage({ id: 'fleetadmin.purpose.maxduration' }),
      dataIndex: 'maxDuration',
      render: (value) => <span>{formatISODuration(value)}</span>,
      sorter: alphabeticalSorterFor('maxDuration'),
    },
    {
      title: intl.formatMessage({ id: 'fleetadmin.purpose.legalbasis' }),
      dataIndex: 'legalBasis',
      width: 300,
      render: (value) => <span>{truncateText(value, 77)}</span>,
      sorter: alphabeticalSorterFor('legalBasis'),
    },
    {
      title: intl.formatMessage({ id: 'fleetadmin.purpose.fieldgroupkeys' }),
      dataIndex: 'fieldGroupKeys',
      render: renderGroups,
      sorter: alphabeticalSorterFor('fieldGroupKeys'),
    },
  ];
};

export default getPurposesColumns;
